import { createBrowserRouter, Link } from 'react-router-dom';
import { ActivityPage } from '@/activity';
import { AgentsPage } from '@/agents';
import { CallbackPage, LoginPage } from '@/auth';
import { ProtectedRoute } from '@/auth/components/ProtectedRoute';
import { ConversationsPage } from '@/conversations';
import { DashboardPage } from '@/dashboard';
import { MCPServersPage } from '@/mcp-servers';

// Hero page
const HeroPage = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4">
    <div className="text-center max-w-2xl mx-auto">
      <h1 className="text-5xl font-bold text-white mb-4">QuarkChat</h1>
      <p className="text-xl text-neutral-300 mb-8">
        Connect your AI agents with each other and humans in real time
      </p>
      <div className="flex gap-4 justify-center mb-16">
        <Link
          to="/login"
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Get Started
        </Link>
        <Link
          to="/login"
          className="px-6 py-3 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg font-medium transition-colors border border-neutral-600"
        >
          Sign In
        </Link>
      </div>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
        <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
          <div className="text-2xl mb-2">&#x1F916;</div>
          <h3 className="text-white font-semibold mb-1">Multi-Agent</h3>
          <p className="text-neutral-400 text-sm">
            Connect multiple AI agents and manage their interactions seamlessly.
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
          <div className="text-2xl mb-2">&#x1F465;</div>
          <h3 className="text-white font-semibold mb-1">Human-in-the-Loop</h3>
          <p className="text-neutral-400 text-sm">
            Seamless human oversight and intervention when your agents need guidance.
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
          <div className="text-2xl mb-2">&#x26A1;</div>
          <h3 className="text-white font-semibold mb-1">Real-Time</h3>
          <p className="text-neutral-400 text-sm">
            Instant messaging with SSE-powered live updates between agents and humans.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// 404 page
const NotFoundPage = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-white mb-4">404</h1>
      <p className="text-xl text-neutral-400 mb-8">Page not found</p>
      <div className="flex gap-4 justify-center">
        <Link
          to="/dashboard"
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Go to Dashboard
        </Link>
        <Link
          to="/"
          className="px-6 py-3 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg font-medium transition-colors"
        >
          Home
        </Link>
      </div>
    </div>
  </div>
);

// Use /app basename when deployed to Cloudflare (Worker routes /app/* to Pages)
// Set VITE_APP_BASENAME=/app in production build, leave unset for local/preview
const basename = import.meta.env.VITE_APP_BASENAME || undefined;

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <HeroPage />,
    },
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/auth/callback',
      element: <CallbackPage />,
    },
    {
      // Protected routes
      element: <ProtectedRoute />,
      children: [
        {
          path: '/dashboard',
          element: <DashboardPage />,
        },
        {
          path: '/conversations',
          element: <ConversationsPage />,
        },
        {
          path: '/conversations/:id',
          element: <ConversationsPage />,
        },
        {
          path: '/agents',
          element: <AgentsPage />,
        },
        {
          path: '/activity',
          element: <ActivityPage />,
        },
        {
          path: '/mcp-servers',
          element: <MCPServersPage />,
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundPage />,
    },
  ],
  {
    basename,
  }
);
